import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import AppDownloadQR from "../../components/AppDownloadQR";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  FaCodeBranch,
  FaEdit,
  FaExclamationTriangle,
  FaFilter,
  FaMoneyBillWave,
  FaMotorcycle,
  FaPlus,
  FaSearch,
  FaStar,
  FaTimes,
  FaTrashAlt,
  FaTruck,
  FaUserCheck,
  FaUsers,
  FaWifi,
} from "react-icons/fa";

import { deleteRider, getAllRiders } from "../../actions/ridersAction";
import socket from "../../utilities/socket";
import { getUserFromLocalStorage } from "utilities/user";
import { usePermissions } from "../../utilities/usePermissions";
import { openCodCashReceipt } from "../../utilities/codCashReceiptBus";
import RiderForm from "views/Forms/RiderForm";
import "./RidersTable.css";
import AppSelect from "../../components/ui/AppSelect";

const RidersTable = () => {
  const { riders = [], loading } = useSelector((state) => state.riders);
  const user = getUserFromLocalStorage();
  const dispatch = useDispatch();
  const history = useHistory();
  const { can, loaded: permissionsLoaded } = usePermissions();
  const canManageRiders = permissionsLoaded && can("riders:manage");
  const canCreateRiders =
    canManageRiders || (permissionsLoaded && can("riders:create"));
  const canUpdateRiders =
    canManageRiders || (permissionsLoaded && can("riders:update"));
  const canDeleteRiders =
    canManageRiders || (permissionsLoaded && can("riders:delete"));
  // Receiving a rider's cash is money handling: the server requires wallet:manage.
  const canReceiveCash = permissionsLoaded && can("wallet:manage");

  const [showRiderForm, setShowRiderForm] = useState(false);
  const [riderToEdit, setRiderToEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [riderToDelete, setRiderToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [codFlagFilter, setCodFlagFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");

  useEffect(() => {
    dispatch(getAllRiders());
  }, [dispatch]);

  useEffect(() => {
    const handleRiderStatusUpdate = (statusUpdate) => {
      dispatch({ type: "UPDATE_RIDER_STATUS", payload: statusUpdate });
    };
    const handleRiderPerformanceUpdate = (performanceUpdate) => {
      dispatch({ type: "UPDATE_RIDER_PERFORMANCE", payload: performanceUpdate });
    };

    socket.emit("joinAdmin");
    socket.on("riderStatusUpdate", handleRiderStatusUpdate);
    socket.on("rider:performanceUpdated", handleRiderPerformanceUpdate);

    return () => {
      socket.off("riderStatusUpdate", handleRiderStatusUpdate);
      socket.off("rider:performanceUpdated", handleRiderPerformanceUpdate);
      socket.emit("leave", "adminRoom");
    };
  }, [dispatch]);

  const carrierTypes = useMemo(
    () => [...new Set(riders.map((rider) => rider.carrierType).filter(Boolean))],
    [riders]
  );

  const fleetMetrics = useMemo(() => {
    const online = riders.filter((rider) => rider.onlineStatus === "Online").length;
    const partner = riders.filter(
      (rider) => rider.partnerIds?.length > 0 || rider.partnerId
    ).length;
    const codFlagged = riders.filter((rider) => rider.codCash?.isFlagged).length;

    return { online, partner, codFlagged };
  }, [riders]);

  const filteredRiders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return riders.filter((rider) => {
      const matchesSearch =
        !query ||
        `${rider.firstName || ""} ${rider.lastName || ""}`
          .toLowerCase()
          .includes(query) ||
        String(rider.mobile || "").toLowerCase().includes(query) ||
        rider.email?.toLowerCase().includes(query) ||
        rider.vendorName?.toLowerCase().includes(query) ||
        rider.partnerName?.toLowerCase().includes(query) ||
        rider.vehicleDetails?.plateNumber?.toLowerCase().includes(query);
      const matchesStatus =
        statusFilter === "all" || rider.onlineStatus === statusFilter;
      const matchesCarrier =
        carrierFilter === "all" || rider.carrierType === carrierFilter;
      const matchesCod =
        codFlagFilter === "all" ||
        (codFlagFilter === "flagged" && rider.codCash?.isFlagged) ||
        (codFlagFilter === "clear" && !rider.codCash?.isFlagged);
      const hasPartner = rider.partnerIds?.length > 0 || Boolean(rider.partnerId);
      const matchesPartner =
        partnerFilter === "all" ||
        (partnerFilter === "partner" && hasPartner) ||
        (partnerFilter === "direct" && !hasPartner);

      return (
        matchesSearch &&
        matchesStatus &&
        matchesCarrier &&
        matchesCod &&
        matchesPartner
      );
    });
  }, [
    carrierFilter,
    codFlagFilter,
    partnerFilter,
    riders,
    searchQuery,
    statusFilter,
  ]);

  const hasActiveFilters =
    Boolean(searchQuery) ||
    statusFilter !== "all" ||
    carrierFilter !== "all" ||
    codFlagFilter !== "all" ||
    partnerFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setCarrierFilter("all");
    setCodFlagFilter("all");
    setPartnerFilter("all");
  };

  const fmtDate = (date) => {
    if (!date) return "Not recorded";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fmtDateTime = (date) => {
    if (!date) return "Never";
    const parsedDate = new Date(date);
    return `${parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })} ${parsedDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const handleShowRiderForm = (rider = null) => {
    if (rider ? !canUpdateRiders : !canCreateRiders) return;
    setRiderToEdit(rider);
    setShowRiderForm(true);
  };

  const handleCloseRiderForm = () => {
    setRiderToEdit(null);
    setShowRiderForm(false);
  };

  const handleShowDeleteConfirm = (rider) => {
    if (!canDeleteRiders) return;
    setRiderToDelete(rider);
    setShowDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setRiderToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = () => {
    if (riderToDelete && canDeleteRiders) {
      dispatch(deleteRider(riderToDelete._id, user?.result?._id));
    }
    handleCloseDeleteConfirm();
  };

  return (
    <main className="riders-workspace">
      {loading && (
        <div className="riders-loading" role="status" aria-label="Loading riders">
          <Spinner animation="border" size="sm" />
          <span>Updating fleet</span>
        </div>
      )}

      <header className="riders-workspace__header">
        <div className="riders-workspace__title">
          <span className="riders-workspace__eyebrow">Delivery operations</span>
          <h1>
            <span aria-hidden="true">
              <FaMotorcycle />
            </span>
            Active Riders
          </h1>
          <p>Monitor availability, affiliations, performance, and COD standing.</p>
        </div>
        <div className="riders-workspace__actions">
          <button
            type="button"
            className="riders-button riders-button--ghost"
            onClick={() => history.push("/admin/3pl-partners")}
          >
            <FaCodeBranch /> 3PL partners
          </button>
          <button
            type="button"
            className="riders-button riders-button--primary"
            onClick={() => handleShowRiderForm()}
            disabled={!canCreateRiders}
          >
            <FaPlus /> Add rider
          </button>
        </div>
      </header>

      <section className="riders-metrics" aria-label="Fleet summary">
        <article className="riders-metric riders-metric--blue">
          <div>
            <span>Total riders</span>
            <strong>{riders.length}</strong>
          </div>
          <span className="riders-metric__icon">
            <FaUsers />
          </span>
        </article>
        <article className="riders-metric riders-metric--green">
          <div>
            <span>Online now</span>
            <strong>{fleetMetrics.online}</strong>
          </div>
          <span className="riders-metric__icon">
            <FaWifi />
          </span>
        </article>
        <article className="riders-metric riders-metric--gold">
          <div>
            <span>3PL affiliated</span>
            <strong>{fleetMetrics.partner}</strong>
          </div>
          <span className="riders-metric__icon">
            <FaCodeBranch />
          </span>
        </article>
        <article className="riders-metric riders-metric--coral">
          <div>
            <span>COD attention</span>
            <strong>{fleetMetrics.codFlagged}</strong>
          </div>
          <span className="riders-metric__icon">
            <FaExclamationTriangle />
          </span>
        </article>
      </section>

      {/* Onboarding: which app each role installs — reusable cards, links live
          in config/appLinks.js. Rider app first; customer apps for reference. */}
      <AppDownloadQR
        title="Onboard a rider — scan to install the apps"
        apps={["rider-android", "customer-android", "customer-ios"]}
        compact
      />

      <section className="riders-panel">
        <div className="riders-panel__heading">
          <div>
            <span>Fleet directory</span>
            <strong>
              {filteredRiders.length} of {riders.length} riders shown
            </strong>
          </div>
          <div className="riders-live-note">
            <span aria-hidden="true" />
            Live rider status
          </div>
        </div>

        <div className="riders-toolbar">
          <label className="riders-search">
            <FaSearch aria-hidden="true" />
            <span className="sr-only">Search riders</span>
            <input
              type="search"
              placeholder="Search name, contact, partner, or plate"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </label>

          <div className="riders-filter-grid">
            <label>
              <span>Status</span>
              <AppSelect
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="Online">Online</option>
                <option value="Busy">Busy</option>
                <option value="inTransit">In transit</option>
                <option value="Offline">Offline</option>
              </AppSelect>
            </label>
            <label>
              <span>Carrier</span>
              <AppSelect
                value={carrierFilter}
                onChange={(event) => setCarrierFilter(event.target.value)}
              >
                <option value="all">All carriers</option>
                {carrierTypes.map((carrierType) => (
                  <option key={carrierType} value={carrierType}>
                    {carrierType}
                  </option>
                ))}
              </AppSelect>
            </label>
            <label>
              <span>Affiliation</span>
              <AppSelect
                value={partnerFilter}
                onChange={(event) => setPartnerFilter(event.target.value)}
              >
                <option value="all">All affiliations</option>
                <option value="partner">3PL partner</option>
                <option value="direct">Lusuku direct</option>
              </AppSelect>
            </label>
            <label>
              <span>COD</span>
              <AppSelect
                value={codFlagFilter}
                onChange={(event) => setCodFlagFilter(event.target.value)}
              >
                <option value="all">All COD states</option>
                <option value="flagged">Needs attention</option>
                <option value="clear">Clear</option>
              </AppSelect>
            </label>
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              className="riders-clear-button"
              onClick={clearFilters}
            >
              <FaFilter /> Clear
            </button>
          )}
        </div>

        <div className="riders-table-scroll">
          <table className="riders-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Rider</th>
                <th>Contact</th>
                <th>Carrier</th>
                <th>Status</th>
                <th>Affiliation</th>
                <th>Performance</th>
                <th>COD</th>
                <th>Joined</th>
                <th>Last login</th>
                <th className="riders-table__actions-heading">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRiders.length === 0 ? (
                <tr>
                  <td colSpan={11}>
                    <div className="riders-empty">
                      <FaMotorcycle />
                      <strong>
                        {hasActiveFilters
                          ? "No riders match these filters"
                          : "No riders have been added"}
                      </strong>
                      <span>
                        {hasActiveFilters
                          ? "Adjust or clear the filters to broaden the fleet view."
                          : "Add the first rider to begin managing delivery operations."}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRiders.map((item, index) => {
                  const hasPartner =
                    item.partnerIds?.length > 0 || Boolean(item.partnerId);
                  const isCodFlagged = item.codCash?.isFlagged;
                  const deliveries =
                    item.performanceMetrics?.totalDeliveries ?? 0;
                  const rating = item.performanceMetrics?.averageRating;
                  const plate = item.vehicleDetails?.plateNumber;
                  const partnerName = item.partnerName || null;
                  const partnerLogo = item.partnerLogo || null;
                  const showVendorSub =
                    !hasPartner &&
                    item.vendorName &&
                    item.vendorName !== "Lusuku Admin";
                  const fullName =
                    `${item.firstName || ""} ${item.lastName || ""}`.trim() ||
                    "Unnamed rider";
                  const initials =
                    `${item.firstName?.[0] || ""}${item.lastName?.[0] || ""}` ||
                    "R";

                  return (
                    <tr key={item._id}>
                      <td className="riders-table__index">{index + 1}</td>
                      <td>
                        <div className="rider-identity">
                          <span className="rider-avatar">{initials}</span>
                          <div>
                            <strong>{fullName}</strong>
                            <span>
                              <FaMotorcycle />
                              {plate || "No plate recorded"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="rider-contact">
                          <strong>{item.mobile || "No mobile"}</strong>
                          <span>{item.email || "No email recorded"}</span>
                        </div>
                      </td>
                      <td>
                        <span className="riders-badge riders-badge--blue">
                          <FaTruck />
                          {item.carrierType || "Unassigned"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`riders-badge ${
                            item.onlineStatus === "Online"
                              ? "riders-badge--green"
                              : item.onlineStatus === "Busy"
                              ? "riders-badge--gold"
                              : item.onlineStatus === "inTransit"
                              ? "riders-badge--blue"
                              : "riders-badge--muted"
                          }`}
                        >
                          <span className="riders-status-dot" />
                          {item.onlineStatus || "Offline"}
                        </span>
                      </td>
                      <td>
                        {hasPartner ? (
                          <div className="rider-affiliation">
                            {partnerLogo ? (
                              <img
                                src={partnerLogo}
                                alt=""
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <span className="rider-affiliation__icon">
                                <FaCodeBranch />
                              </span>
                            )}
                            <div>
                              <strong>{partnerName || "3PL partner"}</strong>
                              <span>Partner fleet</span>
                            </div>
                          </div>
                        ) : (
                          <div className="rider-affiliation">
                            <span className="rider-affiliation__icon rider-affiliation__icon--direct">
                              <FaUserCheck />
                            </span>
                            <div>
                              <strong>Lusuku direct</strong>
                              {showVendorSub && <span>{item.vendorName}</span>}
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="rider-performance">
                          <strong>{deliveries.toLocaleString()} deliveries</strong>
                          <span>
                            <FaStar />
                            {rating == null
                              ? "Not rated"
                              : Number(rating).toFixed(1)}
                          </span>
                        </div>
                      </td>
                      <td>
                        {isCodFlagged ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`cod-${item._id}`}>
                                Holding UGX{" "}
                                {(item.codCash?.heldBalance || 0).toLocaleString()}
                              </Tooltip>
                            }
                          >
                            <span className="riders-badge riders-badge--red riders-badge--help">
                              <FaExclamationTriangle /> Attention
                            </span>
                          </OverlayTrigger>
                        ) : (
                          <span className="riders-badge riders-badge--green">
                            Clear
                          </span>
                        )}
                      </td>
                      <td className="riders-table__date">
                        {fmtDate(item.createdAt)}
                      </td>
                      <td className="riders-table__date riders-table__date--muted">
                        {fmtDateTime(item.lastLogin)}
                      </td>
                      <td>
                        <div className="riders-row-actions">
                          {canReceiveCash && (item.codCash?.heldBalance || 0) > 0 && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`cash-${item._id}`}>
                                  Receive UGX {(item.codCash?.heldBalance || 0).toLocaleString()} cash
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="riders-icon-button"
                                onClick={() =>
                                  openCodCashReceipt({
                                    mode: "rider",
                                    riderId: item._id,
                                    riderName: fullName,
                                    heldBalance: item.codCash?.heldBalance || 0,
                                    onDone: () => dispatch(getAllRiders()),
                                  })
                                }
                                aria-label={`Receive cash from ${fullName}`}
                              >
                                <FaMoneyBillWave />
                              </button>
                            </OverlayTrigger>
                          )}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`edit-${item._id}`}>Edit rider</Tooltip>}
                          >
                            <button
                              type="button"
                              className="riders-icon-button"
                              onClick={() => handleShowRiderForm(item)}
                              disabled={!canUpdateRiders}
                              aria-label={`Edit ${fullName}`}
                            >
                              <FaEdit />
                            </button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`delete-${item._id}`}>Delete rider</Tooltip>
                            }
                          >
                            <button
                              type="button"
                              className="riders-icon-button riders-icon-button--danger"
                              onClick={() => handleShowDeleteConfirm(item)}
                              disabled={!canDeleteRiders}
                              aria-label={`Delete ${fullName}`}
                            >
                              <FaTrashAlt />
                            </button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <RiderForm
        show={showRiderForm}
        handleClose={handleCloseRiderForm}
        riderToEdit={riderToEdit}
      />

      <Modal
        show={showDeleteConfirm}
        onHide={handleCloseDeleteConfirm}
        centered
        dialogClassName="riders-delete-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete rider?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="riders-delete-warning">
            <FaExclamationTriangle />
            <div>
              <strong>This removes the rider from active operations.</strong>
              <p>
                {riderToDelete
                  ? `${riderToDelete.firstName || ""} ${
                      riderToDelete.lastName || ""
                    } (${riderToDelete.email || "no email"})`
                  : ""}
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="riders-button riders-button--ghost"
            onClick={handleCloseDeleteConfirm}
          >
            <FaTimes /> Cancel
          </button>
          <button
            type="button"
            className="riders-button riders-button--danger"
            onClick={handleConfirmDelete}
            disabled={!canDeleteRiders}
          >
            <FaTrashAlt /> Delete rider
          </button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default RidersTable;
